/**
 * Convierte texto plano con marcas de Markdown a HTML
 */
export function convertMarkdownToHtml(content: string): string {
  if (!content) return ''
  
  // Verificar si ya es HTML válido (contiene etiquetas HTML de formato)
  const hasHtmlTags = /<p>|<h[1-6]>|<ul>|<ol>|<li>|<strong>|<em>/i.test(content)
  if (hasHtmlTags) {
    return content
  }
  
  // También verificar si contiene caracteres de Markdown pero no está formateado como HTML
  const hasMarkdownSyntax = /(^|\n)#{1,6}\s|(\*\*|\*|^-|\d+\.)/m.test(content)
  if (!hasMarkdownSyntax) {
    // Si no tiene sintaxis de markdown ni HTML, solo wrap en párrafos
    const paragraphs = content.split('\n').filter(line => line.trim()).map(line => `<p>${line.trim()}</p>`)
    return paragraphs.join('\n')
  }
  
  // Convertir Markdown a HTML
  let html = content
  
  // Headers - procesar en orden inverso para evitar conflictos
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>')
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>')
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>')
  
  // Texto en negrita (antes que cursiva para evitar conflictos)
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  
  // Texto en cursiva
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>')
  
  // Listas con viñetas
  const bulletListMatches = html.match(/^-\s+(.*)$/gim)
  if (bulletListMatches) {
    let processedHtml = html
    bulletListMatches.forEach(match => {
      const listItem = match.replace(/^-\s+/, '<li>').replace(/$/, '</li>')
      processedHtml = processedHtml.replace(match, listItem)
    })
    // Wrap list items in ul tags
    processedHtml = processedHtml.replace(/<li>.*?<\/li>(?:\s*<li>.*?<\/li>)*/gs, '<ul>$&</ul>')
    html = processedHtml
  }
  
  // Listas numeradas
  const numberedListMatches = html.match(/^\d+\.\s+(.*)$/gim)
  if (numberedListMatches) {
    let processedHtml = html
    numberedListMatches.forEach(match => {
      const listItem = match.replace(/^\d+\.\s+/, '<li>').replace(/$/, '</li>')
      processedHtml = processedHtml.replace(match, listItem)
    })
    // Wrap list items in ol tags
    processedHtml = processedHtml.replace(/<li>.*?<\/li>(?:\s*<li>.*?<\/li>)*/gs, '<ol>$&</ol>')
    html = processedHtml
  }
  
  // Dividir en párrafos el resto del contenido
  const lines = html.split('\n').filter(line => line.trim())
  const paragraphs = lines.map(line => {
    if (line.includes('<h1>') || line.includes('<h2>') || line.includes('<h3>') || 
        line.includes('<ul>') || line.includes('<ol>') || line.includes('<li>') ||
        line.includes('<strong>') || line.includes('<em>')) {
      return line
    }
    return `<p>${line.trim()}</p>`
  })
  
  return paragraphs.join('\n')
}

/**
 * Renderiza HTML de manera segura (solo permite etiquetas básicas)
 */
export function safeRenderHtml(html: string): { __html: string } {
  // Lista de etiquetas HTML permitidas
  const allowedTags = ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'a', 'img']
  
  // Remover todas las etiquetas no permitidas y atributos peligrosos
  let safeHtml = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
  safeHtml = safeHtml.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
  safeHtml = safeHtml.replace(/on\w+="[^"]*"/gi, '') // Remover event handlers
  safeHtml = safeHtml.replace(/on\w+='[^']*'/gi, '') // Remover event handlers
  
  // Solo permitir etiquetas de la lista
  const tagPattern = new RegExp(`<(?!/?(?:${allowedTags.join('|')})\b)[^>]*>`, 'gi')
  safeHtml = safeHtml.replace(tagPattern, '')
  
  // Limpiar atributos en etiquetas permitidas
  safeHtml = safeHtml.replace(/<(\w+)([^>]*)>/g, (match, tag, attrs) => {
    if (!allowedTags.includes(tag)) return ''
    
    // Solo permitir href en links e src en imágenes
    let safeAttrs = ''
    if (tag === 'a') {
      const hrefMatch = attrs.match(/href=["']([^"']*)["']/)
      if (hrefMatch) {
        safeAttrs = ` href="${hrefMatch[1]}"`
      }
    } else if (tag === 'img') {
      const srcMatch = attrs.match(/src=["']([^"']*)["']/)
      if (srcMatch) {
        safeAttrs = ` src="${srcMatch[1]}"`
      }
    }
    
    return `<${tag}${safeAttrs}>`
  })
  
  return { __html: safeHtml }
}